import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CountryValidator } from 'src/app/shared/services/country.validator';
import { ActivatedRoute, Router } from '@angular/router';

import { DateTime } from 'luxon';
import { DataService } from 'src/app/shared/services/data.service';
import { SingleResultApiResponse } from 'src/app/shared/models/api-response.model';
import { Store, select } from '@ngrx/store';
import { Action_SetCurrentApplication, Action_StudentValidationForNewApplication } from '../state/actions';
import { Subscription } from 'rxjs';
import { ErrorResponse } from 'src/app/shared/models/error.model';
import { Country } from 'src/app/shared/models/lookup-data.model';


@Component({
  selector: 'online-app-student-validation',
  templateUrl: './student-validation.component.html',
  styleUrls: ['./student-validation.component.scss']
})
export class StudentValidationComponent implements OnInit {

  days = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
  months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  years: any[] = [];

  studentValidationFormGroup: FormGroup;
  countries: Country[];

  isFormSubmitted: Boolean = false;


  storeSubscription: Subscription;

  constructor(private activatedRoute: ActivatedRoute,
    private dataService: DataService,
    private store: Store<any>,
    private router: Router,
    private formBuilder: FormBuilder) {
    for (let year = 1990; year < 2018; year++) {
      this.years.push(year);
    }
  }

  ngOnInit() {

    this.countries = this.activatedRoute.snapshot.data['countries'];

    this.studentValidationFormGroup = this.formBuilder.group({
      givenName: ['', Validators.required],
      familyName: ['', Validators.required],
      dateOfBirth: this.formBuilder.group({
        day: ['', Validators.required],
        month: ['', Validators.required],
        year: ['', Validators.required],
      }),
      country: ['', [Validators.required, CountryValidator(this.countries)]]
    });

  }

  cancelStudentValidation(): void {
    this.router.navigate(['/applications', 'dashboard']);
  }

  validateStudent(): void {

    this.isFormSubmitted = true;

    const day = this.studentValidationFormGroup.get('dateOfBirth.day').value;
    const month = this.studentValidationFormGroup.get('dateOfBirth.month').value;
    const year = this.studentValidationFormGroup.get('dateOfBirth.year').value;

    this.validateDate(day, month, year);


    if (this.studentValidationFormGroup.valid) {
      this.validateStudentInfo();
    }

  }

  validateStudentInfo(): void {

    const familyName = this.studentValidationFormGroup.get('familyName').value;
    const dateOfBirth = this.getDateOfBirth();
    const countryCode = this.studentValidationFormGroup.get('country').value;
    const givenName = this.studentValidationFormGroup.get('givenName').value;

    this.dataService.ValidateStudentParentsInfo(familyName, givenName, dateOfBirth, countryCode)
      .subscribe(
        (response: SingleResultApiResponse) => {
          if (response.Errors && response.Errors.length > 0) {
            console.log(`Error: ${response.Errors}`);
          } else {

            const applicationId = response.Result.ApplicationId;
            const customerReferenceNumber = response.Result.CustomerReferenceNumber;

            this.store.dispatch({
              type: Action_SetCurrentApplication,
              payload: applicationId
            });

            this.router.navigate(['/applications', applicationId, 'edit', 'important-information']);
          }

        },
        (errorResponse: ErrorResponse) => {
          const errors = errorResponse.errors;
          if (errors && errors.length > 0) {
            const errorKey = errors[0].Key;
            if (errorKey === 'model') {

              this.store.dispatch({
                type: Action_StudentValidationForNewApplication,
                payload: {
                  'StudentGivenName': givenName,
                  'StudentFamilyName': familyName,
                  'StudentDateOfBirth': dateOfBirth,
                  'StudentNationality': countryCode
                }
              });

              this.router.navigate(['/applications', 'parents-validation']);
            }
          }

        }
      );
  }

  validateDate(day: number, month: string, year: number): void {
    const monthIndex = this.getMonthIndex(month);
    const dateOfBirthValidity = DateTime.fromObject({ month: monthIndex, day: +day, year: +year });
    if (!dateOfBirthValidity.isValid) {
      // this.invalidDateOfBirthReason = dateOfBirthValidity.invalidReason;
      this.studentValidationFormGroup.get('dateOfBirth.day').setErrors({ 'invalidDay': true });
    }
  }

  getDateOfBirth(): string {
    const day = this.studentValidationFormGroup.get('dateOfBirth.day').value;
    const month = this.studentValidationFormGroup.get('dateOfBirth.month').value;
    const year = this.studentValidationFormGroup.get('dateOfBirth.year').value;
    const monthIndex = this.getMonthIndex(month);
    return `${day}/${monthIndex}/${year}`;
  }

  getMonthIndex(month: string): number {
    let monthIndex = 0; // Range: 1 to 12; 0 means invalid
    switch (month) {
      case 'Jan':
        monthIndex = 1;
        break;
      case 'Feb':
        monthIndex = 2;
        break;
      case 'Mar':
        monthIndex = 3;
        break;
      case 'Apr':
        monthIndex = 4;
        break;
      case 'May':
        monthIndex = 5;
        break;
      case 'Jun':
        monthIndex = 6;
        break;
      case 'Jul':
        monthIndex = 7;
        break;
      case 'Aug':
        monthIndex = 8;
        break;
      case 'Sep':
        monthIndex = 9;
        break;
      case 'Oct':
        monthIndex = 10;
        break;
      case 'Nov':
        monthIndex = 11;
        break;
      case 'Dec':
        monthIndex = 12;
        break;
    }

    return monthIndex;
  }

  //show border when tab on link for accessibility
  linkTabAccessibility(event): void {
    event.style.outline = "1px solid black";
  }

  linkBlurAccessibility(event): void {
    event.style.outline = "";
  }

  //accessibility -- link error msg to form field
  errorDoubleErrorAccessibility(formControlName: string, requireErrorId: string, apiErrorId: string): string {
    if (this.isFormSubmitted &&
      this.studentValidationFormGroup.get(formControlName).errors &&
      this.studentValidationFormGroup.get(formControlName).errors.required) {
      return requireErrorId;
    } else if (this.isFormSubmitted &&
      this.studentValidationFormGroup.get(formControlName).errors &&
      this.studentValidationFormGroup.get(formControlName).errors.invalidDay) {
      return apiErrorId;
    } else {
      return "";
    }
  }

  errorCountryAccessibility(formControlName: string): string {
    if (this.isFormSubmitted &&
      this.studentValidationFormGroup.get(formControlName).errors &&
      this.studentValidationFormGroup.get(formControlName).errors.required) {
      return "requiredCountry";
    } else if (this.isFormSubmitted &&
      this.studentValidationFormGroup.get(formControlName).errors &&
      this.studentValidationFormGroup.get(formControlName).errors.blockedCountry) {
      return "blockedCountry";
    } else if (this.isFormSubmitted &&
      this.studentValidationFormGroup.get(formControlName).errors &&
      this.studentValidationFormGroup.get(formControlName).errors.regularCountry) {
      return "regularCountry";
    } else if (this.isFormSubmitted &&
      this.studentValidationFormGroup.get(formControlName).errors &&
      this.studentValidationFormGroup.get(formControlName).errors.apiValidationError) {
      return "apiErrorCountry";
    } else {
      return "";
    }
  }
}
